import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import analyticsApi from '../../api/analytics.js';
import Hidden from '@material-ui/core/Hidden';

import PortalAppBar from '../../components/PortalAppBar/PortalAppBar.js';
import BlockModal from '../../components/BlockModal/BlockModal';
import ContactModal from '../../components/ContactModal/ContactModal';
import ExternalLinkModal from './ExternalLinkModal/ExternalLinkModal';
import SurveyModal from './SurveyModal/SurveyModal';
import ReplaceSponsoredData from '../../components/Replace/ReplaceSponsoredData/ReplaceSponsoredData';
import ReplaceContact from '../../components/Replace/ReplaceContact/ReplaceContact';
import { withStyles } from '@material-ui/core/styles';

import styles from './styles.js';
import {
  actions as routingActions,
  types as routingTypes,
} from '../../ducks/routing.js';
import { actions as authActions } from '../../ducks/auth.js';
import { actions as appActions } from '../../ducks/app.js';
import { actions as customerActions } from '../../ducks/customer.js';
import { getString } from '../../helpers/stringHelpers.js';
import configureDrawerMap from '../../drawerMap.js';
import theme from '../../theme.js';
import { hasRole } from '../../helpers/authHelpers.js';
import { isFeatureDisabled } from '../../helpers/outageHelpers';
import GlobalDrawer from '../../components/PortalAppBar/GlobalDrawer/GlobalDrawer';
import GlobalDrawerList from '../../components/PortalAppBar/GlobalDrawer/GlobalDrawerList/GlobalDrawerList';
import TermsDialog from '../../components/TermsDialog/TermsDialog';
import { get } from 'lodash-es';
import moment from 'moment';
import preferencesAPI from '../../api/preferences';
import NZBankValidator from '@fnzc/nz-bank-account-validator';
import InvalidBankDialog from '../../components/InvalidBankDialog/InvalidBankDialog.js';

// App wide polyfills.
import 'blueimp-canvas-to-blob';

const stringsComponent = 'app';

class App extends React.Component {
  constructor(props) {
    super(props);

    this.pageContainerOffset = React.createRef();

    this.state = {
      externalLinkModalOpen: false,
      externalLinkUrl: null,
      initialLoad: true,
      lastGAPageViewPath: null,
      showSurvey: false,
      showBanner: portalForm ? portalForm.map((form) => form.id) : [],
    };

    this.handleCancelConfirm = this.handleCancelConfirm.bind(this);
    this.navigate = this.navigate.bind(this);
    this.openUrl = this.openUrl.bind(this);
    this.toggleExternalLinkModal = this.toggleExternalLinkModal.bind(this);
    this.sendGAPageView = this.sendGAPageView.bind(this);
    this.isTermsAccepted = this.isTermsAccepted.bind(this);
    this.acceptTerms = this.acceptTerms.bind(this);
    this.setPageMargin = this.setPageMargin.bind(this);
    this.setSurveyViewIndicator = this.setSurveyViewIndicator.bind(this);
    this.handleSurveyComplete = this.handleSurveyComplete.bind(this);
    this.handleSurveyDismiss = this.handleSurveyDismiss.bind(this);
    this.handleSurveyReject = this.handleSurveyReject.bind(this);
    this.handleSurveyRemindMeLater = this.handleSurveyRemindMeLater.bind(this);
    this.showBanner = this.showBanner.bind(this);
    this.hideBanner = this.hideBanner.bind(this);
  }

  handleCancelConfirm() {
    const { ok, currentPage, accountType } = this.props;
    const category =
      currentPage === routingTypes.WORK_REQUESTS_CREATE
        ? 'Maintenance'
        : currentPage === routingTypes.ARRANGEMENT_CREATE &&
          accountType === 'rent'
        ? 'Rent Arrangement'
        : currentPage === routingTypes.ARRANGEMENT_CREATE &&
          accountType === 'maintenance'
        ? 'Maintenance Arrangement'
        : 'Profile';

    ok();
    analyticsApi.event(category, 'Click', 'Cancel');
  }

  navigate(event) {
    if (event.target instanceof Element) {
      const anchor = event.target.closest('a');
      if (anchor !== null) {
        const href = anchor.getAttribute('href');
        const isExternalURL = href.startsWith('http')
          ? new URL(anchor.getAttribute('href')).origin !== location.origin
          : false;
        if (anchor.classList.contains('external') || isExternalURL) {
          const stripTrailingSlash = (str) => {
            return str.endsWith('/') ? str.slice(0, -1) : str;
          };
          // Check if url is in the external link exceptions, stripping the trailing / off to do the comparison
          // Don't show the leaving sponsored data warning if there is an exception for this link
          if (
            !externalLinkExceptions.some(
              (url) => stripTrailingSlash(url) === stripTrailingSlash(href)
            )
          ) {
            event.preventDefault();
            this.toggleExternalLinkModal();
            this.setState({ externalLinkUrl: anchor.getAttribute('href') });
          }
        }
      }
    }
  }

  openUrl() {
    const { externalLinkUrl } = this.state;
    window.open(externalLinkUrl);
    this.toggleExternalLinkModal();
  }

  toggleExternalLinkModal() {
    const { externalLinkModalOpen } = this.state;
    this.setState({ externalLinkModalOpen: !externalLinkModalOpen });
  }

  sendGAPageView() {
    const { currentPath, routeChangeTime } = this.props;
    const { initialLoad, lastGAPageViewPath } = this.state;

    // Calculate page load time based on when route change was requested.
    let pageLoadTime = Date.now() - routeChangeTime;
    if (initialLoad) {
      const perfData = window.performance.timing;
      const serverLoadTime = perfData.loadEventEnd - perfData.navigationStart;
      pageLoadTime += serverLoadTime > 0 ? serverLoadTime : 0;
      this.setState({ initialLoad: !initialLoad });
    }

    if (lastGAPageViewPath !== currentPath) {
      analyticsApi.pageView(currentPath, pageLoadTime);
      this.setState({ lastGAPageViewPath: currentPath });
    }
  }

  acceptTerms() {
    const { app } = this.props;

    // Remove milliseconds from datetime string
    const currentDateTime = moment().toISOString(true).split('.')[0];

    const data = {
      id: latestTerms.id,
      type: 'terms',
      version: latestTerms.version,
      action: 'accepted',
      date: currentDateTime,
    };

    preferencesAPI.createOrUpdateViewIndicator(data);
    app.setTermsAccepted();
  }

  isTermsAccepted() {
    const { termsAccepted, preferences } = this.props;

    if (termsAccepted) {
      return true;
    } else {
      // Use preferences view indicator to determine whether to show terms and conditions or not
      const termsAlreadyAccepted =
        preferences.data.viewIndicators &&
        preferences.data.viewIndicators.find(
          (vi) =>
            vi.type === 'terms' &&
            vi.version === latestTerms.version &&
            vi.action === 'accepted'
        );

      return termsAlreadyAccepted;
    }
  }

  /**
   * Dynamically set the page margin, as the fixed header can change height
   * depending on the breadcrumb height.
   */
  setPageMargin() {
    if (this.pageContainerOffset.current) {
      const portalAppBar = document.getElementById('PortalAppBar');
      if (portalAppBar) {
        // @todo Look into a nicer solution than retrieving the element by ID.
        this.pageContainerOffset.current.style.marginTop = `${
          document.getElementById('PortalAppBar').offsetHeight - 50
        }px`;
      }
    }
  }

  isBankAccountInvalid(accountNumber) {
    let [bank, branch, account, suffix] = accountNumber.split('-');
    return !NZBankValidator.isValidNZBankNumber(bank, branch, account, suffix);
  }

  handleSurveyDismiss(id) {
    const { reference } = this.props;

    const form = portalForm && portalForm.find((pf) => pf.id == id);
    if (!form) {
      return;
    }

    const key = reference + '.' + 'dismissed-survey' + id;
    localStorage.setItem(key, true);

    this.hideBanner(id);

    const dismissCount = form.dismissCount;

    const {
      preferences: {
        data: { viewIndicators },
      },
    } = this.props;

    // Use version to store how many times survey was dismissed
    // After configured dismissals, reject
    const viewIndicator =
      viewIndicators && viewIndicators.find((vi) => vi.id == 'survey' + id);
    if (viewIndicator && viewIndicator.action == 'dismissed') {
      analyticsApi.event(
        'Survey',
        'Click',
        `Dismiss ${viewIndicator.version + 1}`
      );
      if (viewIndicator.version < dismissCount - 1) {
        this.setSurveyViewIndicator('dismissed', id, viewIndicator.version + 1);
      } else if (viewIndicator.version >= dismissCount - 1) {
        this.handleSurveyReject(id);
      }
    } else {
      analyticsApi.event('Survey', 'Click', 'Dismiss 1');
      if (1 >= dismissCount - 1) {
        this.handleSurveyReject(id);
      } else {
        this.setSurveyViewIndicator('dismissed', id, 1);
      }
    }
  }

  handleSurveyRemindMeLater(id) {
    this.hideBanner(id);
    setTimeout(() => {
      this.showBanner(id);
    }, 180000);
    analyticsApi.event('Survey', 'Click', 'Remind me later');
  }

  handleSurveyReject(id) {
    this.setSurveyViewIndicator('rejected', id, 1);
  }

  handleSurveyComplete(id) {
    this.setSurveyViewIndicator('completed', id, 1);
    analyticsApi.event('Survey', 'Click', 'Complete');
  }

  setSurveyViewIndicator(action, id, version) {
    // Remove milliseconds from datetime string
    const currentDateTime = moment().toISOString(true).split('.')[0];

    const viewIndicatorId = 'survey' + id;

    const data = {
      id: viewIndicatorId,
      type: 'survey',
      version: version ? version : 1,
      action: action,
      date: currentDateTime,
    };

    preferencesAPI.createOrUpdateViewIndicator(data);
  }

  hideBanner(id) {
    this.setState({
      showBanner: this.state.showBanner.filter((formId) => formId != id),
    });
  }

  showBanner(id) {
    const banners = this.state.showBanner;
    banners.push(id);
    this.setState({
      showBanner: banners,
    });
  }

  render() {
    const {
      dispatch,
      authenticated,
      error,
      page,
      appBarTitle,
      customerName,
      show,
      message,
      cancel,
      routing,
      app,
      customer: { addViewedSurvey },
      tenancy,
      showContactModal,
      currentPage,
      currentPath,
      roles,
      classes,
      reference,
      customerFirstName,
      preferences,
      termsAccepted,
      letters,
      viewedSurveys,
      bankDetails,
    } = this.props;

    const { showSurvey } = this.state;

    const bindRouteAction = (action) => () =>
      bindActionCreators(action, dispatch)();
    const logoutAction = bindRouteAction(authActions.signout);

    const { externalLinkModalOpen } = this.state;

    let appointmentsCount = 0;
    if (tenancy.loaded == 'LOADED') {
      const appointments = get(
        tenancy.data,
        'currentTenancy.appointments',
        null
      );
      appointmentsCount = appointments ? appointments.length : 0;
    }

    let lettersCount = 0;
    if (
      letters.loaded == 'LOADED' &&
      preferences.loaded == 'LOADED' &&
      letters.data.hasOwnProperty('letters')
    ) {
      if (preferences.data.viewIndicators) {
        letters.data.letters.forEach((letter) => {
          if (
            preferences.data.viewIndicators &&
            !preferences.data.viewIndicators.some(
              (vi) => letter.letterId == vi.id
            ) &&
            !preferences.viewedLetters.includes(letter.letterId)
          ) {
            lettersCount++;
          }
        });
      } else {
        // If the user has letters but no read letter view indicators yet.
        lettersCount = Object.keys(letters.data.letters).length;
      }
    }

    const drawerMap = configureDrawerMap(
      currentPage,
      dispatch,
      appointmentsCount,
      lettersCount,
      roles
    );

    const Page = page;

    let showTerms = false;

    if (preferences.loaded === 'LOADED' && tenancy.loaded === 'LOADED') {
      if (tenancy.data.firstLogin && !termsAccepted) {
        showTerms = false;
        this.acceptTerms();
      } else {
        showTerms = !this.isTermsAccepted();
      }
    }

    const termsDialog = showTerms ? (
      <TermsDialog acceptTerms={this.acceptTerms} logoutAction={logoutAction} />
    ) : null;

    const shouldShowBanner = (form) => {
      const key = this.props.reference + '.dismissed-survey' + form.id;
      if (localStorage.getItem(key)) {
        return false;
      }

      if (!preferences.data.viewIndicators) {
        return true;
      }

      const vi = preferences.data.viewIndicators.find((vi) => {
        if (vi.id == 'survey' + form.id) {
          if (vi.action == 'rejected' || vi.action == 'completed') {
            // Show survey again after configured months have passed
            const now = moment();
            const surveyDate = moment(vi.date);
            const diff = now.diff(surveyDate, 'months');

            if (
              (vi.action == 'rejected' && diff >= form.rejectMonths) ||
              (vi.action == 'completed' && diff >= form.completeMonths)
            ) {
              return false;
            } else {
              return true;
            }
          } else {
            return false;
          }
        } else {
          return false;
        }
      });

      return !vi;
    };

    const activeForm =
      preferences.loaded === 'LOADED' &&
      !isFeatureDisabled('FORM_WRITE', tenancy.data.area) &&
      portalForm &&
      portalForm.find(
        (form) =>
          (form.path == 'all' || '/' + form.path == currentPath) &&
          shouldShowBanner(form) &&
          this.state.showBanner.includes(form.id)
      );

    const showInvalidBankDialog =
      (bankDetails.loaded === 'LOADED' &&
        bankDetails.data.length == 1 &&
        this.isBankAccountInvalid(bankDetails.data[0].accountNumber)) ||
      (bankDetails.data.length == 2 &&
        (this.isBankAccountInvalid(bankDetails.data[0].accountNumber) ||
          this.isBankAccountInvalid(bankDetails.data[1].accountNumber)));

    const invalidBankDialog = showInvalidBankDialog ? (
      <InvalidBankDialog toggleContactModal={app.toggleContactModal} />
    ) : null;

    this.setPageMargin();

    return (
      <div>
        <div onClick={this.navigate}>
          {(authenticated || error) && (
            <React.Fragment>
              <PortalAppBar
                title={appBarTitle}
                drawerMap={drawerMap.filter(
                  (item) => !item || !item.role || hasRole(roles, item.role)
                )}
                customerName={customerName}
                logoutAction={logoutAction}
                logoClick={routing.goDash}
                reference={reference}
                customerFirstName={customerFirstName}
                gaEvent={analyticsApi.event}
                bannerMessage={activeForm ? activeForm.bannerMessage : ''}
                bannerOnYes={(e) => {
                  this.setState({ showSurvey: true });
                  e.preventDefault();
                }}
                bannerOnNo={() => {
                  this.handleSurveyDismiss(activeForm.id);
                }}
                bannerOnRemindLater={() => {
                  this.handleSurveyRemindMeLater(activeForm.id);
                }}
                showBanner={!!activeForm}
              />
            </React.Fragment>
          )}
          {Page ? (
            <div
              className={classes.pageContainerOffset}
              ref={this.pageContainerOffset}
            >
              <div className={classes.pageContainer}>
                {authenticated || error ? (
                  <Hidden smDown>
                    <GlobalDrawer
                      open={true}
                      toggleDrawer={() => {}}
                      list={<GlobalDrawerList items={drawerMap} />}
                      handleLogoClick={this.handleLogoClick}
                      logoutAction={logoutAction}
                    />
                  </Hidden>
                ) : (
                  ''
                )}
                <div className={classes.contentWrapper}>
                  <Page onLoadComplete={this.sendGAPageView} />
                </div>
              </div>
            </div>
          ) : (
            ''
          )}
          <BlockModal
            show={show}
            message={message}
            cancel={cancel}
            ok={this.handleCancelConfirm}
            currentPage={currentPage}
            secondaryButtonLabel={
              currentPage == routingTypes.WORK_REQUESTS_CREATE
                ? getString('blockModal', 'BUTTON_LABEL_CANCEL_JOB')
                : getString('blockModal', 'BUTTON_LABEL_CANCEL')
            }
          />
          <ContactModal
            open={showContactModal}
            handleClose={app.toggleContactModal}
          />
          <ExternalLinkModal
            title={portalContent.EXTERNAL_LINK_MODAL_TITLE}
            content={
              <ReplaceSponsoredData
                content={portalContent.EXTERNAL_LINK_MODAL_CONTENT}
                linkUrl={portalContent.APP_SPONSORED_DATA_LINK}
              />
            }
            open={externalLinkModalOpen}
            handleProceed={this.openUrl}
            proceedLabel={getString(
              stringsComponent,
              'EXTERNAL_LINK_MODAL_PROCEED_LABEL'
            )}
            handleBack={this.toggleExternalLinkModal}
            backLabel={getString(
              stringsComponent,
              'EXTERNAL_LINK_MODAL_BACK_LABEL'
            )}
          />
          {showSurvey && activeForm ? (
            <SurveyModal
              id={activeForm.id}
              confirmationHeader={activeForm.confirmationHeader}
              confirmationBody={activeForm.confirmationBody}
              form={JSON.parse(activeForm.json)}
              path={currentPath}
              viewIndicators={preferences.data.viewIndicators}
              handleSurveyComplete={this.handleSurveyComplete}
              handleSurveyReject={this.handleSurveyReject}
              handleSurveyDismiss={this.handleSurveyDismiss}
              onClose={() => {
                this.setState({ showSurvey: false });
                this.hideBanner(activeForm.id);
              }}
              onCancel={() => {
                this.setState({ showSurvey: false });
                analyticsApi.event('Survey', 'Click', 'Cancel');
              }}
              failureText={
                <ReplaceContact
                  content={portalContent.APP_ERROR_MESSAGE}
                  action={app.toggleContactModal}
                />
              }
            />
          ) : null}
          {termsDialog}
          {invalidBankDialog}
        </div>
      </div>
    );
  }
}

function mapStateToProps(state) {
  return {
    page: state.routing.page,
    appBarTitle: state.app.appBarTitle,
    authenticated: state.auth.authenticated,
    roles: state.auth.roles,
    error: state.auth.error,
    inOutage: state.app.inOutage,
    outageFrom: state.app.outageFrom,
    outageTo: state.app.outageTo,
    customerName: `${state.auth.firstName} ${state.auth.lastName}`,
    customerFirstName: state.auth.firstName,
    reference: state.auth.reference,
    show: !!state.app.message,
    message: state.app.message,
    cancel: () => state.app.canLeave(false),
    ok: () => state.app.canLeave(true),
    tenancy: state.customer.tenancy,
    showContactModal: state.app.showContactModal,
    currentPage: state.location.type,
    currentPath: state.location.pathname,
    accountType: state.app.accountType,
    routeChangeTime: state.app.routeChangeTime,
    preferences: state.customer.preferences,
    termsAccepted: state.app.termsAccepted,
    letters: state.customer.letters,
    viewedSurveys: state.customer.viewedSurveys,
    bankDetails: state.customer.bankDetails,
  };
}

const mapDispatchToProps = (dispatch) => ({
  routing: bindActionCreators({ ...routingActions }, dispatch),
  app: bindActionCreators({ ...appActions }, dispatch),
  customer: bindActionCreators({ ...customerActions }, dispatch),
  dispatch,
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withStyles(styles, { withTheme: true })(App));
